/**
 * Display formatting for timestamps that come FROM THE BACKEND
 * (`createdAt`, `startedAt`, `completedAt`, `receivedAt`, `sentAt`, the
 * statistics day-buckets — anything serialised by the NestJS API).
 *
 * ──────────────────────────────────────────────────────────────────────────
 *   ⚠️  TIMEZONE CONVENTION — READ THIS BEFORE RENDERING ANY BACKEND DATE  ⚠️
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Every timestamp lives in a Postgres `timestamp` column WITHOUT time zone,
 * and Drizzle/NestJS serialise it with `Date.toISOString()` — so the API
 * always hands us a `Z`-tagged ISO string. What that `Z` is worth depends on
 * HOW THE COLUMN WAS WRITTEN (see the next section): the wire format is the
 * same for both kinds, the meaning is not.
 *
 * Whatever the column, NEVER bare-format a backend date: a plain
 * `toLocaleString('de-DE')` (no `timeZone`) applies the VIEWER'S browser zone,
 * so the same value reads differently on a laptop in Hamburg and one in
 * Denver. Both helpers below pin an explicit zone and are therefore
 * deterministic.
 *
 *     ✅  formatBackendDateTime(x)                -> "09.06.2026, 15:37:00"  (Ziffern verbatim)
 *     ✅  formatInstantInGermanTime(x)            -> "09.06.2026, 17:37"     (Instant in Berlin)
 *     ❌  x.toLocaleString('de-DE')               -> Browser-Zone, WRONG
 *     ❌  new Date(x).toLocaleTimeString('de-DE') -> derselbe Fallstrick
 *
 * NB: this convention is about *display only*. Do not "fix" the backend to
 * shift instants, and do not strip the `Z` on the wire.
 *
 * ──────────────────────────────────────────────────────────────────────────
 *  WELCHER HELFER? — die Spaltenherkunft entscheidet
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Die Prod-DB-Session läuft in `Europe/Berlin`, nicht in UTC (eine ältere
 * Behauptung in CLAUDE.md war hier falsch). Dadurch ist die gespeicherte
 * Wanduhr PRO SPALTE verschieden:
 *
 *  1. Postgres setzt den Wert selbst (`DEFAULT now()`, niemand schreibt die
 *     Spalte) — der Normalfall bei `createdAt`: dort steht bereits die
 *     BERLINER Wanduhr. Das `Z` auf der Leitung ist für diese Spalten eine
 *     Fiktion; wer sie in UTC rendert, sieht exakt die gespeicherten Ziffern
 *     — und die sind schon deutsche Zeit.
 *       → {@link formatBackendDate} / {@link formatBackendDateTime} /
 *         {@link formatBackendTime} (auf UTC gepinnt = Ziffern verbatim).
 *
 *  2. Die App schreibt den Wert als JS-`Date` (`new Date()` bzw. ein
 *     Provider-Datum) — `incoming_email_av_forward.sentAt` / `.queuedAt` /
 *     `.acknowledgedAt`, `incoming_email_attachment.scanErrorReportedAt`,
 *     `stuecklisten_scan_file.scanErrorReportedAt`,
 *     `incoming_email_job.receivedAt`: dort landet die UTC-Wanduhr, das `Z`
 *     ist also ein echter Instant. In UTC gerendert liegt er im Sommer 2 h,
 *     im Winter 1 h hinter dem, was Disponent und Kunde auf der Uhr sehen
 *     (OpenProject #193: „an AV weitergeleitet … 07:09" statt „09:09").
 *     Ebenfalls Fall 2, obwohl gar keine Spalte: `linkExpiresAt` auf der
 *     Erfassungs-Seite ist das `exp` des Signatur-Tokens.
 *       → {@link formatInstantInGermanTime} (auf Europe/Berlin gepinnt).
 *
 * Entscheidend ist also die SCHREIBSTELLE, nicht der Spalten-Default: ein
 * `DEFAULT now()` im Schema genügt nicht (`incoming_email_av_forward.queuedAt`
 * und `customer_email_domain.createdAt` haben einen, werden aber von der App
 * gesetzt → Fall 2), und umgekehrt hat `stuecklisten_scan_file.startedAt` gar
 * keinen und ist trotzdem Fall 2. Auch ein `createdAt` ist also kein
 * Freifahrtschein — im Zweifel die Schreibstelle im Backend suchen.
 *
 * Die ältere, weiterhin gültige Ausnahme `incoming_email_job.receivedAt`
 * (Instant von Resend, in der Disposition gegen Outlook abgeglichen,
 * Kunden-Meeting 2026-07-08: „Datum/Uhrzeit wie in Outlook") ist damit kein
 * Sonderfall mehr, sondern schlicht der erste Vertreter von Fall 2.
 *
 * ──────────────────────────────────────────────────────────────────────────
 *  RELATIVZEITEN („vor 3 Minuten") — Fall 1 braucht eine echte Umrechnung
 * ──────────────────────────────────────────────────────────────────────────
 *
 * Alles bisher Gesagte ist FORMATIERUNG: eine Zone anpinnen, Ziffern
 * ausgeben. `moment(x).fromNow()` / `dayjs(x).fromNow()` formatieren aber
 * nicht, sie RECHNEN — sie ziehen zwei Instants voneinander ab. Daran lässt
 * sich keine Zone anpinnen, und damit bricht die Fiktion aus Fall 1 („das `Z`
 * bedeutet nichts, lies die Ziffern verbatim"): die Berliner Wanduhr, als UTC
 * gelesen, liegt ein bis zwei Stunden in der ZUKUNFT. Ein gerade angelegter
 * Vorgang stand deshalb mit „in 2 Stunden" in der Bestellungs-Liste
 * (OpenProject #200; im Winter „in 1 Stunde").
 *
 * Fall-1-Werte müssen für eine Relativzeit also erst in den Instant
 * übersetzt werden, den sie meinen:
 *
 *     ✅  moment(backendWallClockToInstant(row.createdAt)).fromNow()
 *     ❌  moment(row.createdAt).fromNow()
 *
 * {@link backendWallClockToInstant} gilt ausschließlich für Fall 1. Auf einen
 * Fall-2-Wert angewandt verschöbe sie einen bereits korrekten Instant ein
 * zweites Mal — dort gehört der Rohwert unverändert in `fromNow()`.
 *
 * Auch das ist nur ein Pflaster auf der DB-Session-Zeitzone. Der saubere Weg
 * bleibt die zurückgestellte Stufe 2 von #193: die Session-Zeitzone im
 * gemeinsamen Connector (`@repo/database`) auf UTC festnageln und die
 * Bestandsdaten migrieren. Danach sind alle Spalten echte Instants, diese
 * Funktion entfällt ersatzlos und die Fall-1-Formatierer werden zu
 * {@link formatInstantInGermanTime}.
 *
 * Bewusst NICHT umgestellt: die Scanner- und Statistik-Oberflächen
 * (`pages/pdf_scanner_v4/**`, `components/scan/scan_timeline.tsx`, …) zeigen
 * weiterhin durchgängig die gespeicherte Wanduhr, auch wo einzelne Spalten
 * (`startedAt`, `completedAt`) app-geschrieben sind. Das ist die
 * Scanner-Konvention „UI == gespeicherter Wert"; sie sauber aufzulösen hängt
 * an der DB-Session-Zeitzone (zurückgestellte Stufe 2 von #193).
 *
 * Eine einzige Ausnahme von dieser Scanner-Konvention:
 * `stuecklisten_scan_file.scanErrorReportedAt` in
 * `pages/pdf_scanner_v4/detail_page/job_detail_file_detail_v4.tsx`. Derselbe
 * „Gemeldet am"-Wert erscheint auch im Bestelleingang (`mail_panel.tsx`, die
 * Zwillingsspalte `incoming_email_attachment.scanErrorReportedAt`); beide
 * Stellen dürfen nicht zwei Stunden auseinanderliegen.
 *
 * Ebenfalls unangetastet: `customer_email_domain.createdAt`
 * (`pages/email-settings/domain-recognition/**`). Diese Spalte hat gemischte
 * Herkunft — beim ersten Anlegen DB-Default, beim Re-Assign app-geschrieben —
 * und braucht erst eine Entscheidung auf der Schreibseite.
 */

/**
 * The zone the "stored wall-clock" helpers render in — pinning to UTC shows
 * the stored digits verbatim. See case 1 in the file header.
 */
export const BACKEND_DISPLAY_TIME_ZONE = 'UTC';

/** The default locale used across the German-language UI. */
const DEFAULT_LOCALE = 'de-DE';

export type BackendDateInput = string | number | Date | null | undefined;

function toDate(value: BackendDateInput): Date | null {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a `DEFAULT now()` backend timestamp, always pinned to
 * {@link BACKEND_DISPLAY_TIME_ZONE} — i.e. the stored digits verbatim.
 * That is case 1 in the file header; app-written columns belong on
 * {@link formatInstantInGermanTime}.
 *
 * Pass any `Intl.DateTimeFormatOptions` to control the shape (date-only,
 * time-only, long weekday, …); omit them for the locale default (date + time).
 * Returns `""` for `null` / `undefined` / unparseable input.
 *
 *   formatBackendDate(x)                                     -> "09.06.2026, 15:37:00"
 *   formatBackendDate(x, { day: '2-digit', month: '2-digit', year: 'numeric' }) -> "09.06.2026"
 *   formatBackendDate(x, { hour: '2-digit', minute: '2-digit' })                -> "15:37"
 */
export function formatBackendDate(
  value: BackendDateInput,
  options?: Intl.DateTimeFormatOptions,
  locale: string = DEFAULT_LOCALE,
): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleString(locale, { ...options, timeZone: BACKEND_DISPLAY_TIME_ZONE });
}

/** Date + time in the locale default shape — "09.06.2026, 15:37:00". */
export function formatBackendDateTime(value: BackendDateInput, locale: string = DEFAULT_LOCALE): string {
  return formatBackendDate(value, undefined, locale);
}

/** Time only — "15:37" (or "15:37:02" with `seconds: true`). */
export function formatBackendTime(
  value: BackendDateInput,
  opts: { seconds?: boolean } = {},
  locale: string = DEFAULT_LOCALE,
): string {
  return formatBackendDate(
    value,
    { hour: '2-digit', minute: '2-digit', ...(opts.seconds ? { second: '2-digit' } : {}) },
    locale,
  );
}

/**
 * The business's zone — deterministic, NOT the viewer's browser zone, so the
 * ban on bare `toLocaleString` still stands. See case 2 in the file header.
 */
export const GERMAN_BUSINESS_TIME_ZONE = 'Europe/Berlin';

/** Outlook-Form "09.06.2026, 17:37" — gilt, wenn keine Options übergeben werden. */
const GERMAN_INSTANT_DEFAULT_SHAPE: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * Format an app-written backend timestamp — a true instant — pinned to
 * {@link GERMAN_BUSINESS_TIME_ZONE}. That is case 2 in the file header:
 * `sentAt`, `acknowledgedAt`, `scanErrorReportedAt`, `receivedAt`.
 *
 * ⚠️ `shape` heißt `shape` und nicht `options`, weil es die Default-Form
 * ERSETZT und nicht in sie hineingemischt wird — gleicher Vertrag wie bei
 * {@link formatBackendDate}, wo die Options ebenfalls die ganze Form
 * bestimmen, aber ANDERS als bei {@link formatBackendTime}, das seine
 * Zusatz-Flags mischt. `{ second: '2-digit' }` ergibt hier also „09" und
 * nicht „17:37:09": wer Sekunden oder das Jahr braucht, gibt die ganze Form
 * an. Ohne `shape` gilt die Outlook-Form.
 *
 *   formatInstantInGermanTime(x)                                         -> "09.06.2026, 17:37"
 *   formatInstantInGermanTime(x, { day: '2-digit', month: '2-digit' })   -> "09.06."
 *   formatInstantInGermanTime(x, { hour: '2-digit', minute: '2-digit' }) -> "17:37"
 *
 * Returns `""` for `null` / `undefined` / unparseable input.
 */
export function formatInstantInGermanTime(
  value: BackendDateInput,
  shape?: Intl.DateTimeFormatOptions,
  locale: string = DEFAULT_LOCALE,
): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleString(locale, {
    ...(shape ?? GERMAN_INSTANT_DEFAULT_SHAPE),
    timeZone: GERMAN_BUSINESS_TIME_ZONE,
  });
}

/**
 * Zerlegt einen Instant in seine Berliner Wanduhr-Bestandteile, in einer
 * festen, gut rückparsbaren Form. `hourCycle: 'h23'` ist nicht kosmetisch:
 * ohne ihn liefern Engines für Mitternacht je nach Locale „24" statt „00".
 * Gelesen wird über `part.type`, die Locale bestimmt also nur die Ziffern.
 *
 * Einmal gebaut, weil das Konstruieren eines `Intl.DateTimeFormat` teuer ist
 * und `formatToParts` billig — die Bestellungs-Liste ruft das pro Zeile auf.
 */
const BERLIN_PARTS_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: GERMAN_BUSINESS_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/**
 * Der UTC-Versatz von `Europe/Berlin` ZU DIESEM INSTANT, in Millisekunden
 * (+1 h in der Normalzeit, +2 h in der Sommerzeit).
 *
 * Bewusst aus der IANA-Zeitzonendatenbank abgeleitet statt fest verdrahtet:
 * nur so sitzt der Sprung an den echten Umstellungstagen (letzter Sonntag im
 * März bzw. Oktober) und überlebt künftige Regeländerungen — ein
 * hartkodiertes „Sommer = 2 h" wäre an jedem Umstellungswochenende falsch.
 */
function berlinUtcOffsetMsAt(instant: Date): number {
  // `formatToParts` wirft auf einem `Invalid Date`. Das kann hier nur der
  // Sondierungs-Aufruf unten auslösen (Zeitstempel am Rand des
  // Date-Wertebereichs, plus/minus zwei Tage = außerhalb) — als NaN
  // weitergereicht fällt das Ergebnis am Ende sauber auf `null`.
  if (Number.isNaN(instant.getTime())) return Number.NaN;
  const parts = BERLIN_PARTS_FORMATTER.formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value);
  // Die Berliner Wanduhr dieses Instants, ihrerseits wieder als UTC gelesen —
  // die Differenz zum Instant IST der Versatz. Millisekunden trägt `Intl`
  // nicht mit, die kommen deshalb aus dem Original zurück (sonst wäre der
  // Versatz um bis zu 999 ms daneben).
  const berlinWallClockAsUtcMs = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour'),
    read('minute'),
    read('second'),
    instant.getUTCMilliseconds(),
  );
  return berlinWallClockAsUtcMs - instant.getTime();
}

/**
 * Übersetzt einen Fall-1-Zeitstempel — Postgres hat ihn selbst gesetzt
 * (`DEFAULT now()`), in der Spalte steht die BERLINER Wanduhr, auf der
 * Leitung trägt sie ein fiktives `Z` — in den echten Instant, den er meint.
 *
 * Gedacht für Relativzeiten (`moment(...).fromNow()`), die rechnen statt zu
 * formatieren; siehe den Abschnitt „Relativzeiten" im Datei-Header und
 * OpenProject #200. Für reine Anzeige bleibt es bei
 * {@link formatBackendDateTime} — die Ziffern sind ja schon deutsche Zeit.
 *
 * ⚠️ NICHT für Fall-2-Spalten (`sentAt`, `receivedAt`, `acknowledgedAt`, …):
 * deren Wert IST bereits ein Instant und würde hier ein zweites Mal
 * verschoben.
 *
 *   backendWallClockToInstant('2026-07-15T07:09:00.000Z') // -> 05:09:00Z (CEST, −2 h)
 *   backendWallClockToInstant('2026-01-15T07:09:00.000Z') // -> 06:09:00Z (CET,  −1 h)
 *
 * Gibt `null` für `null` / `undefined` / unparsbare Eingaben zurück — analog
 * zum `""` der Formatierer oben, damit Aufrufer nicht crashen.
 */
export function backendWallClockToInstant(value: BackendDateInput): Date | null {
  const wallClock = toDate(value);
  if (!wallClock) return null;
  const wallClockMs = wallClock.getTime();

  // `toDate` hat die Ziffern als UTC gelesen — und genau diese Ziffern sind
  // die gesuchte Wanduhr. Gesucht ist der Instant t mit
  //     Berliner Wanduhr(t) == Ziffern,   also   t = Ziffern − Versatz(t).
  // Der Versatz hängt am Ergebnis, ist also nicht direkt ausrechenbar. Er ist
  // aber einer von höchstens zwei Werten: die Zeitzone kennt rund um den
  // Zielzeitpunkt nur Normal- und Sommerzeit. Beide Kandidaten holen wir uns
  // je zwei Tage vor und nach dem Rohwert — großzügig mehr, als zwischen
  // Wanduhr und Instant je liegen kann, und damit garantiert beidseits jeder
  // Umstellung in der Nähe. Bewusst so statt „+1 h / +2 h" fest zu verdrahten:
  // die Werte kommen aus der IANA-Datenbank, der Sprung sitzt am echten
  // Umstellungstag und überlebt eine Regeländerung.
  const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;
  const candidateOffsets = [
    berlinUtcOffsetMsAt(new Date(wallClockMs - TWO_DAYS_MS)),
    berlinUtcOffsetMsAt(new Date(wallClockMs + TWO_DAYS_MS)),
  ];

  // Gültig ist ein Kandidat, der sich selbst bestätigt: der Instant, den er
  // ergibt, muss in Berlin auch wirklich diesen Versatz haben.
  const confirmed = candidateOffsets.filter(
    (offset) => berlinUtcOffsetMsAt(new Date(wallClockMs - offset)) === offset,
  );

  // In den zwei Umstellungsstunden des Jahres ist eine Wanduhr nicht eindeutig
  // invertierbar: im Oktober kommt 02:30 zweimal vor (beide Kandidaten
  // bestätigen sich), im März gar nicht (keiner bestätigt sich). Beide Male
  // gewinnt der GRÖSSERE Versatz, also der FRÜHERE Instant — und das ist
  // keine Geschmacksfrage: die späte Lesart läge für eine gerade geschriebene
  // Zeile eine Stunde in der Zukunft und brächte damit in der Umstellungs-
  // nacht exakt das „in 1 Stunde" zurück, das dieses Ticket abstellt.
  const offset = Math.max(...(confirmed.length > 0 ? confirmed : candidateOffsets));

  const instant = new Date(wallClockMs - offset);
  // Versatz aus einer unvollständigen `Intl`-Ausgabe (theoretisch) oder ein
  // Zeitstempel am Rand des Date-Wertebereichs ergäben `Invalid Date`. Die
  // Aufrufer prüfen auf `null`, und ein `Invalid Date` ist truthy — hier also
  // sauber abfangen statt „Invalid date" ins UI durchzureichen.
  return Number.isNaN(instant.getTime()) ? null : instant;
}
